import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Client, CognitoUser, TrackXAPIPayload, _ } from '@mira-avant/mtlibs';
import { AuthService } from './auth.service';
import { ClientService } from './client.service';
import { ApiService } from './api.service';
import { NavigationService } from './navigation.service';

export interface XApiEventResult {
	completed: boolean;
	duration?: number;
	score?: {
		raw: number;
		max: number;
	};
}

@Injectable({
	providedIn: 'root',
})
export class ReportingService {
	private readonly BASE_PATH = 'reporting';
	private previousRoute: string | undefined;

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor(
		private api: ApiService,
		private authService: AuthService,
		private clientService: ClientService,
		private nav: NavigationService
	) {}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public navigateToReports(): void {
		//When we navigate to reports first store our current route in
		//this.previousRoute but only if the pathname doesn't include 'reports'
		this.previousRoute = window.location.pathname.includes('reports')
			? undefined
			: window.location.pathname;

		this.nav.navigate(['/', 'my', 'reports']);
	}

	public navigateBack(): void {
		//If we have a previous route, navigate to it
		if (this.previousRoute) {
			this.nav.navigateToPathName(this.previousRoute);

			//Clear the previous route
			this.previousRoute = undefined;
		} else {
			//Otherwise navigate to the journey
			this.nav.navigate(['/', 'my', 'journey']);
		}
	}

	public async trackXapi$(
		xapiData: Omit<TrackXAPIPayload, 'student_cognito_uuid' | 'client_uuid'>
	): Promise<void> {
		return this.withLoading(async () => {
			const user: CognitoUser | null = await firstValueFrom(this.authService.user$);

			if (!user) {
				throw new Error('No user found');
			}

			const client: Client = await firstValueFrom(this.clientService.selectedClient$);

			await firstValueFrom(
				this.api.post<void>(`${this.BASE_PATH}/track-xapi`, {
					...xapiData,
					student_cognito_uuid: user.uuid,
					client_uuid: client.uuid,
				})
			);
		});
	}

	public processXApiEvent(event: any): XApiEventResult {
		const result = event.data?.statement?.result;

		if (!result) {
			return { completed: false };
		}

		return {
			completed: result.completion || false,
			duration: this.convertIso8601DurationToSeconds(result.duration),
			score: result.score,
		};
	}

	private convertIso8601DurationToSeconds(duration: string): number | undefined {
		if (!duration) return undefined;

		// ISO8601 duration regex pattern
		const pattern = /P(?:(\d+)D)?T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/i;
		const matches = duration.match(pattern);

		if (!matches) return undefined;

		const [, days = '0', hours = '0', minutes = '0', seconds = '0'] = matches;

		return (
			parseInt(days) * 86400 +
			parseInt(hours) * 3600 +
			parseInt(minutes) * 60 +
			parseFloat(seconds)
		);
	}
}
